import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const sections = [
  {
    id: 'intro',
    title: 'Introduction',
    icon: '🤝',
    content: [
      { type: 'text', value: 'JamboRicG is a community platform built on the spirit of Ubuntu — the belief that we are who we are through others. We connect people to support one another in times of joy, hardship, and disaster.' },
      { type: 'text', value: 'This Privacy Policy explains how we collect, use, and protect the personal information you share with us when using the JamboRicG platform, mobile applications, and related services. By using JamboRicG, you agree to the practices described in this policy.' },
    ]
  },
  {
    id: 'collection',
    title: 'Information We Collect',
    icon: '📋',
    content: [
      { type: 'heading', value: 'Personal Information' },
      { type: 'bullet', value: 'Full name, email address, phone number, and national ID (where required for verification)' },
      { type: 'bullet', value: 'Profile photo and biographical information you choose to share' },
      { type: 'bullet', value: 'Bank account or mobile money details for contribution and disbursement purposes' },
      { type: 'heading', value: 'Community Activity' },
      { type: 'bullet', value: 'Group memberships, contributions made, and assistance received across Kileleni, Mbeleko, Majanga, Uvunaji, Urithi, and other modules' },
      { type: 'bullet', value: 'Messages and communications within community groups' },
      { type: 'bullet', value: 'Disaster relief requests and willingness pledges' },
      { type: 'heading', value: 'Technical Information' },
      { type: 'bullet', value: 'Device type, operating system, and app version' },
      { type: 'bullet', value: 'IP address, location data (if permitted), and usage logs' },
      { type: 'bullet', value: 'Crash reports and performance diagnostics' },
      
    ]
  },
  {
    id: 'use',
    title: 'How We Use Your Information',
    icon: '⚙️',
    content: [
      { type: 'text', value: 'Your information is used exclusively to provide and improve the JamboRicG community experience:' },
      { type: 'bullet', value: 'Community Operations: Facilitating contributions, group management, disaster relief coordination, and mutual aid across all platform modules' },
      { type: 'bullet', value: 'Identity Verification: Confirming member identities to protect the integrity of community funds and prevent fraud' },
      { type: 'bullet', value: 'Communications: Sending transaction confirmations, group notifications, disaster alerts, and platform updates' },
      { type: 'bullet', value: 'Customer Support: Resolving disputes, responding to queries, and assisting members in distress' },
      { type: 'bullet', value: 'Platform Improvement: Analyzing usage patterns in aggregate, anonymized form to improve features and reliability' },
      { type: 'bullet', value: 'Legal Compliance: Meeting regulatory obligations under applicable Tanzanian and East African laws' },
      { type: 'text', value: 'We do not sell, rent, or trade your personal information to third parties for marketing purposes.' },
    ]
  },
  {
    id: 'sharing',
    title: 'Information Sharing',
    icon: '🔗',
    content: [
      { type: 'text', value: 'JamboRicG is built on trust. We share your information only in these limited circumstances:' },
      { type: 'heading', value: 'Within Your Community Groups' },
      { type: 'text', value: 'Members of your Kileleni, Mbeleko, or other groups may see your name, contribution status, and group-related activity as part of transparent community management.' },
      { type: 'heading', value: 'Service Providers' },
      { type: 'text', value: 'We work with trusted partners — payment processors, SMS providers, cloud hosting — who process data solely on our behalf under strict confidentiality agreements.' },
      { type: 'heading', value: 'Legal Requirements' },
      { type: 'text', value: 'We may disclose information when required by law, court order, or to protect the safety of our members and the public.' },
      { type: 'heading', value: 'Disaster & Emergency Situations' },
      { type: 'text', value: 'With your consent, we may share relevant information with emergency responders or relief organizations to facilitate urgent assistance.' },
    ]
  },
  {
    id: 'security',
    title: 'Data Security',
    icon: '🔒',
    content: [
      { type: 'text', value: 'We take the security of community funds and personal data seriously:' },
      { type: 'bullet', value: 'All data is transmitted using TLS/HTTPS encryption' },
      { type: 'bullet', value: 'Financial information is stored using industry-standard encryption at rest' },
      { type: 'bullet', value: 'Access to sensitive data is restricted to authorized personnel only' },
      { type: 'bullet', value: 'Regular security audits and vulnerability assessments are conducted' },
      { type: 'bullet', value: 'Two-factor authentication is available and encouraged for all accounts' },
      { type: 'text', value: 'While we implement strong security measures, no system is completely immune to risk. We encourage members to use strong, unique passwords and to report any suspicious activity immediately.' },
    ]
  },
  {
    id: 'rights',
    title: 'Your Rights',
    icon: '✋',
    content: [
      { type: 'text', value: 'As a JamboRicG member, you have the following rights regarding your personal data:' },
      { type: 'bullet', value: 'Access: Request a copy of the personal data we hold about you' },
      { type: 'bullet', value: 'Correction: Update or correct inaccurate information in your profile' },
      { type: 'bullet', value: 'Deletion: Request deletion of your account and associated data, subject to legal retention requirements' },
      { type: 'bullet', value: 'Portability: Receive your data in a machine-readable format' },
      { type: 'bullet', value: 'Objection: Opt out of non-essential communications and certain data processing activities' },
      { type: 'bullet', value: 'Withdrawal of Consent: Withdraw consent for optional data processing at any time' },
      { type: 'text', value: 'To exercise any of these rights, contact us at privacy@jamboricg.com. We will respond within 30 days.' },
    ]
  },
  {
    id: 'retention',
    title: 'Data Retention',
    icon: '🗓️',
    content: [
      { type: 'text', value: 'We retain your personal data for as long as your account is active and for a period thereafter as required by law:' },
      { type: 'bullet', value: 'Active Account Data: Retained for the lifetime of your account' },
      { type: 'bullet', value: 'Transaction Records: Retained for 7 years for financial compliance purposes' },
      { type: 'bullet', value: 'Disaster Relief Records: Retained for 5 years to support future community planning' },
      { type: 'bullet', value: 'Communications: Retained for 2 years for dispute resolution purposes' },
      { type: 'bullet', value: 'Deleted Accounts: when an account is deleted, the records above are retained on this same schedule but anonymized — see Account Deletion for details' },
      { type: 'text', value: 'When data is no longer required, it is securely deleted or anonymized.' },
    ]
  },
  {
    id: 'children',
    title: "Children's Privacy",
    icon: '👶',
    content: [
      { type: 'text', value: 'JamboRicG is designed for adults aged 18 and above. We do not knowingly collect personal information from children under 18.' },
      { type: 'text', value: 'If a parent or guardian believes their child has provided us with personal information, please contact us immediately at privacy@jamboricg.com and we will promptly delete the data.' },
      { type: 'text', value: 'Community members who register on behalf of family units must ensure that account details represent an adult member of the household.' },
    ]
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    icon: '📝',
    content: [
      { type: 'text', value: 'We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or platform features. When we make significant changes, we will:' },
      { type: 'bullet', value: 'Notify all active members via email and in-app notification' },
      { type: 'bullet', value: 'Display a prominent notice on the platform for at least 30 days' },
      { type: 'bullet', value: 'Update the "Last Updated" date at the top of this page' },
      { type: 'text', value: 'Your continued use of JamboRicG after changes take effect constitutes your acceptance of the updated policy.' },
    ]
  },
  {
  id: 'deletion',
  title: 'Account Deletion',
  icon: '🗑️',
  content: [
    { type: 'text', value: 'You can permanently delete your JamboRicG account and the personal data associated with it at any time. This section explains how to request deletion and exactly what happens to your data.' },
    { type: 'heading', value: 'How to delete your account' },
    { type: 'bullet', value: 'In the app: Open the app and sign in, then go to Profile → Account Settings → Delete Account. Enter your password to confirm — your account is deleted immediately.' },
    { type: 'bullet', value: "Can't access the app: Email privacy@jamboricg.com from your registered email address with the subject \"Account Deletion Request.\" We verify the request and complete deletion within 7 business days." },
    { type: 'heading', value: 'What is deleted immediately' },
    { type: 'bullet', value: 'Profile information: your name, email, phone number, national ID image, and profile photo' },
    { type: 'bullet', value: 'Login credentials: your password and active sessions' },
    { type: 'bullet', value: 'Wallet and reward points: any unclaimed wallet balance or reward points are forfeited and cannot be recovered after deletion' },
    { type: 'bullet', value: 'Referral data: your referral code, referral relationships, and referral earnings' },
    { type: 'bullet', value: 'Community activity: your group memberships and visible activity within Kileleni, Mbeleko, and other modules' },
    { type: 'heading', value: 'What we retain, and for how long' },
    { type: 'text', value: "As noted in Data Retention below, some records can't be deleted immediately because we're required to keep them for compliance, dispute resolution, or community accountability:" },
    { type: 'bullet', value: 'Transaction records: kept for 7 years for financial compliance, with your name and contact details removed so the record can no longer be linked to you' },
    { type: 'bullet', value: 'Disaster relief records: kept for 5 years to support future community planning, anonymized where possible' },
    { type: 'bullet', value: 'Records required by law: kept for as long as Tanzanian or East African regulations require' },
    { type: 'text', value: 'Deletion cannot be reversed. We recommend withdrawing any wallet balance before requesting deletion, since balances are forfeited once the account is removed.' },
  ]
},
  {
    id: 'contact',
    title: 'Contact Us',
    icon: '📬',
    content: [
      { type: 'text', value: 'If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please reach out to us:' },
      { type: 'heading', value: 'JamboRicG Privacy Team' },
      { type: 'bullet', value: 'Email: privacy@jamboricg.com' },
      { type: 'bullet', value: 'Phone: +255 XXX XXX XXX' },
      { type: 'bullet', value: 'Address: Dar es Salaam, Tanzania' },
      { type: 'heading', value: 'Data Protection Officer' },
      { type: 'bullet', value: 'Email: dpo@jamboricg.com' },
      { type: 'text', value: 'We are committed to resolving privacy concerns promptly and transparently. All inquiries are acknowledged within 3 business days.' },
    ]
  }
];

function renderContent(content) {
  return content.map((item, i) => {
    if (item.type === 'heading') {
      return (
        <p key={i} className="text-[#001f3f] font-semibold text-sm mt-5 mb-2 uppercase tracking-wide">
          {item.value}
        </p>
      );
    }
    if (item.type === 'bullet') {
      const parts = item.value.split(': ');
      return (
        <div key={i} className="flex gap-3 mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#001f3f] mt-2 flex-shrink-0" />
          <p className="text-gray-600 text-sm leading-relaxed">
            {parts.length > 1 ? (
              <><span className="font-semibold text-[#001f3f]">{parts[0]}:</span> {parts.slice(1).join(': ')}</>
            ) : item.value}
          </p>
        </div>
      );
    }
    return (
      <p key={i} className="text-gray-600 text-sm leading-relaxed mb-3">
        {item.value}
      </p>
    );
  });
}

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState('intro');
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Top Bar */}
      <div className="bg-[#001f3f] px-6 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white text-sm font-medium hover:text-yellow-300 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-2">
          <img src="/images/ricg_logo.png" alt="JamboRicG" className="h-7" onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="text-yellow-400 font-bold text-sm tracking-widest uppercase">JamboRicG</span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="bg-[#001f3f]">
        <div className="max-w-6xl mx-auto px-6 pt-10 pb-12">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-4 bg-yellow-400" />
            <span className="text-yellow-400 text-xs font-bold tracking-widest uppercase">Legal</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Privacy Policy
          </h1>
          <p className="text-blue-200 text-sm max-w-xl leading-relaxed mb-6">
            We are committed to protecting your privacy and handling your data with transparency, care, and respect — the same values that define our community.
          </p>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-yellow-400 flex items-center justify-center">
                <span className="text-xs">📅</span>
              </div>
              <span className="text-blue-200 text-xs">Last Updated: March 2026</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-yellow-400 flex items-center justify-center">
                <span className="text-xs">🌍</span>
              </div>
              <span className="text-blue-200 text-xs">Tanzania & East Africa</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-yellow-400 flex items-center justify-center">
                <span className="text-xs">📖</span>
              </div>
              <span className="text-blue-200 text-xs">{sections.length} Sections</span>
            </div>
          </div>
        </div>

        {/* Section tabs - mobile */}
        <div className="border-t border-white border-opacity-10 overflow-x-auto">
          <div className="flex w-max md:hidden px-4">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollToSection(s.id)}
                className={`px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === s.id
                    ? 'border-yellow-400 text-yellow-400'
                    : 'border-transparent text-blue-300 hover:text-white'
                }`}
              >
                {s.icon} {s.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 py-8 flex gap-8">

        {/* Sidebar */}
        <aside className="hidden md:block w-56 flex-shrink-0">
          <div className="sticky top-6">
            <p className="text-[#001f3f] text-xs font-bold tracking-widest uppercase mb-3 px-1">
              Contents
            </p>
            <nav className="space-y-0.5">
              {sections.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(s.id)}
                  className={`w-full text-left px-3 py-2.5 text-xs flex items-center gap-2.5 transition-all border-l-2 ${
                    activeSection === s.id
                      ? 'border-[#001f3f] bg-[#001f3f] bg-opacity-5 text-[#001f3f] font-semibold'
                      : 'border-transparent text-gray-500 hover:text-[#001f3f] hover:border-gray-300'
                  }`}
                >
                  <span className="text-sm flex-shrink-0">{s.icon}</span>
                  <span>{s.title}</span>
                </button>
              ))}
            </nav>

            {/* Contact card in sidebar */}
            <div className="mt-8 bg-[#001f3f] p-4">
              <p className="text-yellow-400 text-xs font-bold uppercase tracking-wide mb-2">Questions?</p>
              <p className="text-blue-200 text-xs mb-3 leading-relaxed">Contact our privacy team directly.</p>
              <a
                href="mailto:privacy@jamboricg.com"
                className="block text-center bg-yellow-400 text-[#001f3f] text-xs font-bold py-2 px-3 hover:bg-yellow-300 transition-colors"
              >
                Email Us
              </a>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 space-y-0">
          {sections.map((section, idx) => (
            <div
              key={section.id}
              id={section.id}
              className="scroll-mt-6 mb-px"
            >
              {/* Section header */}
              <div className="flex items-center gap-3 bg-[#001f3f] px-6 py-4">
                <span className="text-yellow-400 text-xs font-bold tracking-widest uppercase">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="w-px h-4 bg-white bg-opacity-20" />
                <span className="text-lg">{section.icon}</span>
                <h2 className="text-white font-semibold text-sm tracking-wide">
                  {section.title}
                </h2>
              </div>

              {/* Section body */}
              <div className="bg-white px-6 py-6 border-b border-gray-100">
                {renderContent(section.content)}
              </div>
            </div>
          ))}

          {/* Footer strip */}
          <div className="bg-[#001f3f] px-6 py-8 mt-0">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-yellow-400 font-bold text-sm mb-1">JamboRicG Community Platform</p>
                <p className="text-blue-200 text-xs">
                  Built on trust, transparency, and the spirit of Ubuntu.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:privacy@jamboricg.com"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 text-[#001f3f] text-xs font-bold hover:bg-yellow-300 transition-colors"
                >
                  📬 privacy@jamboricg.com
                </a>
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white border-opacity-20 text-white text-xs font-medium hover:bg-white hover:bg-opacity-10 transition-colors"
                >
                  ← Back to App
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}